import styled, { css, keyframes } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { baseShadows } from "../../../design-system/tokens/shadows";
import { borderRadius } from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import type { UploadedFileType } from "./PromptInputBar.types";

/** Compact composer (design chat sheet). GAP: 42px / 28px / 30px have no control-size tokens, so rem literals. */
const compactContainer = css`
  min-height: 2.625rem;
  padding-top: 0;
  padding-bottom: 0;
  border-color: ${rawColors.stroke.strong};
  border-radius: ${borderRadius.xl};
`;
const compactIconButton = css`
  width: 1.75rem;
  height: 1.75rem;
`;
const compactSendButton = css`
  width: 1.875rem;
  height: 1.875rem;
  border-radius: ${borderRadius.full};
`;

/** Assistant composer (design UiChat): radius 16, strong stroke, pad 10 10 10 14, soft shadow, hint inside, dimmed when disabled. 14px left pad is the design value (no spacing step). */
const assistantContainer = css`
  padding: ${spacing[2]} ${spacing[2]} ${spacing[2]} 0.875rem;
  border-color: ${rawColors.stroke.strong};
  border-radius: ${borderRadius["2xl"]};
  box-shadow: ${baseShadows.light.xs};

  &[aria-disabled="true"] {
    background: ${rawColors.structural[100]};
    border-color: ${rawColors.stroke[200]};
  }
`;
const assistantSendButton = css`
  border-radius: ${borderRadius.full};
  background: ${rawColors.primary[500]};

  &:disabled {
    background: ${rawColors.primary[200]};
    cursor: not-allowed;
  }
`;

const hiddenScrollbar = css`
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * Figma 6511:23231 "Prompt input" — flat stroke/200 border, no gradient.
 * `$inline` is the resting composer: with no context pill and no attachments
 * there is nothing to stack, so the textarea and the action buttons share one
 * row and the bar keeps its short single-line height.
 */
export const Container = styled.div<{ $inline?: boolean; $compact?: boolean; $assistant?: boolean }>`
  display: flex;
  flex-direction: ${({ $inline }) => ($inline ? "row" : "column")};
  align-items: ${({ $inline }) => ($inline ? "center" : "flex-start")};
  gap: ${spacing[2]};
  padding: ${spacing[2]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  width: 100%;
  box-sizing: border-box;
  ${({ $compact }) => $compact && compactContainer}
  ${({ $assistant }) => $assistant && assistantContainer}
`;

/** Wraps the pill + textarea, full width, above the actions row. */
export const InputColumn = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[1]};
  width: 100%;
  min-width: 0;
  ${({ $inline }) => $inline && "flex: 1 1 0;"}
`;

/** Figma "Block" — shows the widget currently in scope. Omitted entirely at story-level scope. */
export const WidgetContextPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2]};
  background: ${rawColors.structural[100]};
  border-radius: ${borderRadius.md};
  /* border-box so the horizontal padding counts inside max-width — otherwise a
     long story/chart title pushes the pill past the bar's right edge. */
  box-sizing: border-box;
  max-width: 100%;
`;

export const WidgetContextClear = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: ${borderRadius.sm};
  color: ${rawColors.text.lowEmphasis};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${rawColors.text.highEmphasis};
  }

  &:disabled {
    cursor: default;
  }
`;

export const WidgetContextLabel = styled.span`
  flex-shrink: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const WidgetContextName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
`;

/** Recording layout — no gradient/border; sits directly in panel content (Figma 3517:113186). */
export const RecordingLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing[3]};
  width: 100%;
`;

/** Icon buttons sit on their own row below the input, right-aligned — decoupled from text height so short placeholders never crowd the pill. */
export const ActionsRow = styled.div<{ $inline?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing[2]};
  width: ${({ $inline }) => ($inline ? "auto" : "100%")};
  ${({ $inline }) => $inline && "flex: 0 0 auto;"}
`;

/** One-line hint inside the bar, left of the actions (assistant variant). */
export const InlineHint = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  margin-right: auto;
  text-align: left;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
`;

export const PromptInput = styled.textarea`
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  resize: none;
  overflow: hidden;
  min-height: 24px;
  max-height: 96px;
  line-height: 1.5;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
  ${hiddenScrollbar}

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }
`;

export const RecordingInputWrapper = styled.div`
  width: 100%;
  padding: ${spacing[3]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  box-sizing: border-box;
  height: 88px;

  ${PromptInput} {
    width: 100%;
    height: 100%;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    ${typographyMixin("body", 3)}
  }
`;

export const GhostIconButton = styled.button<{ $compact?: boolean }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[1]};
  background: transparent;
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  color: ${rawColors.primary[500]};
  box-sizing: border-box;
  ${({ $compact }) => $compact && compactIconButton}

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }

  &:disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const SendIconButton = styled.button<{ $compact?: boolean; $assistant?: boolean }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[1]};
  background: ${rawColors.primary[900]};
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  color: ${rawColors.text.white};
  box-sizing: border-box;
  ${({ $compact }) => $compact && compactSendButton}
  ${({ $assistant }) => $assistant && assistantSendButton}

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

// ── File chips ─────────────────────────────────────────────────────────────────

export const FileChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  width: 100%;
`;

export const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 6px ${spacing[2]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  max-width: 200px;
`;

export const FileTypeBadge = styled.span<{ $fileType: UploadedFileType }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${borderRadius.xs};
  background: ${({ $fileType }) => {
    if ($fileType === "csv") return rawColors.accent.green500;
    if ($fileType === "pdf") return rawColors.accent.red500;
    return rawColors.text.lowEmphasis;
  }};
  color: ${rawColors.text.white};
  font-size: 7px;
  font-weight: 700;
  letter-spacing: -0.2px;
  line-height: 1;
`;

export const FileChipName = styled.span`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const RemoveFileButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${rawColors.text.lowEmphasis};

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

// ── Waveform (recording mode) ──────────────────────────────────────────────────

const barPulse = keyframes`
  0%, 100% { transform: scaleY(0.4); }
  50%       { transform: scaleY(1); }
`;

export const WaveformRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  width: 100%;
  min-height: 32px;
`;

export const WaveformBarsContainer = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  height: 24px;
`;

export const WaveBar = styled.div<{
  $h: number;
  $dim: boolean;
  $delay: number;
}>`
  flex-shrink: 0;
  flex-grow: 0;
  width: 3px;
  height: ${({ $h }) => $h}px;
  border-radius: 1px;
  background: ${({ $dim }) =>
    $dim ? rawColors.primary[200] : rawColors.primary[500]};
  transform-origin: center;
  animation: ${barPulse} 1.1s ease-in-out infinite;
  animation-delay: ${({ $delay }) => -$delay}s;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const SpinnerIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${spin} 0.75s linear infinite;
`;

export const RecordActionBtn = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[1]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  color: ${rawColors.primary[500]};
  box-sizing: border-box;

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;
